// Grafikkerne: hvilken vMix-spec de tegnes fra, og hvor teksterne kommer fra.
// En tekst kan altid overskrives manuelt i controlleren; ellers hentes den
// fra kampen, og til sidst bruges designets standardtekst.

import type { ComponentType } from "react";
import type { GfxFighter, GfxGraphicId, GfxPage, GfxTimerId } from "@mma/shared";
import type { GtAnim, GtSpec } from "./spec.js";
import { clock, timerRemaining, timerState, type GfxCtx } from "./ctx.js";
import { UrExtras } from "./UrExtras.js";
import { Breakdown, EVENT_LABELS, Final, Flash, Recap, fmtTime } from "./spotter/SpotterGraphics.js";
import urSpec from "./specs/ur.json";
import statsSpec from "./specs/stats.json";
import decisionSpec from "./specs/decision.json";
import nameSpec from "./specs/name.json";
import smallNameSpec from "./specs/smallName.json";
import countdownSpec from "./specs/countdown.json";
import h2hSpec from "./specs/h2h.json";

export type { GfxCtx } from "./ctx.js";

type Auto = (c: GfxCtx) => string | null | undefined;

export interface FieldDef {
  key: string;
  label: string;
  element: string; // tekstelementets navn i vMix-titlen
  auto?: Auto;
  fallback?: string;
  // Flere linjer: skriv " | " mellem linjerne i controlleren.
  multiline?: boolean;
}

export interface GraphicDef {
  id: GfxGraphicId;
  label: string;
  group?: string; // overskrift i controllerens liste
  // vMix-titel (spec) eller en af spotter-grafikkerne (Html).
  spec?: GtSpec;
  Html?: ComponentType<{ ctx: GfxCtx; texts: Record<string, string> }>;
  fields: FieldDef[];
  pages?: Record<GfxPage, string>;
  timer?: GfxTimerId;
  // Gemte navne: "name" udfylder navn, "name+title" også titel.
  presets?: "name" | "name+title";
  // Tekst, synlighed og billeder der følger data.
  dynamic?: (c: GfxCtx) => {
    texts?: Record<string, string>;
    visible?: Record<string, boolean>;
    images?: Record<string, string>;
  };
  // Ekstra indhold i grafikkens lag (rundemarkør og medical break i UR).
  Extra?: ComponentType<{ ctx: GfxCtx }>;
}

const dk = (n: number) => String(n).replace(".", ",");

// Rettelser til vMix-titlernes storyboards ligger her, ikke i specs/, som
// genskabes fra .gtzip-filerne.
const anim = (type: string, object: string, o: Partial<GtAnim> = {}): GtAnim => ({
  type,
  object,
  delay: 0,
  duration: null,
  easing: null,
  direction: null,
  reverse: false,
  ...o,
});

const withStoryboards = (spec: unknown, storyboards: GtSpec["storyboards"]): GtSpec => {
  const s = spec as GtSpec;
  return { ...s, storyboards: { ...s.storyboards, ...storyboards } };
};

// Ny fyldfarve på navngivne rektangler.
const recolor = (spec: unknown, fills: Record<string, string>): GtSpec => {
  const s = spec as GtSpec;
  return {
    ...s,
    layers: s.layers.map((l) => ({
      ...l,
      elements: l.elements.map((e) => (e.type === "rect" && fills[e.name] ? { ...e, fill: fills[e.name]! } : e)),
    })),
  };
};

// Runde flag i samme stil som dem vMix-titlerne brugte (circle-flags, MIT).
const flag = (country: string | null | undefined) =>
  country && /^[a-z]{2}$/i.test(country.trim())
    ? `https://cdn.jsdelivr.net/npm/circle-flags@2.8.3/flags/${country.trim().toLowerCase()}.svg`
    : null;

const fighter = (corner: "blue" | "red", get: (f: GfxFighter) => string | null | undefined): Auto => (c) =>
  c.data.fight ? get(c.data.fight[corner]) : null;

const nextFighter = (corner: "blue" | "red"): Auto => (c) => c.data.next?.[corner].lastName;

function statsFields(corner: "blue" | "red"): FieldDef[] {
  return [
    { key: "name", label: "Navn", element: "Navn", auto: fighter(corner, (f) => f.name) },
    { key: "weightClass", label: "Vægtklasse", element: "Klasse", auto: (c) => c.data.fight?.weightClass },
    { key: "record", label: "Record", element: "REC", auto: fighter(corner, (f) => f.record) },
    {
      key: "weight",
      label: "Vægt",
      element: "Weight",
      auto: fighter(corner, (f) => (f.weighInKg != null ? `Weight: ${dk(f.weighInKg)} kg` : null)),
    },
    {
      key: "height",
      label: "Højde",
      element: "Height",
      auto: fighter(corner, (f) => (f.heightCm != null ? `Height: ${f.heightCm} cm` : null)),
    },
    {
      key: "reach",
      label: "Rækkevidde",
      element: "Reach",
      auto: fighter(corner, (f) => (f.reachCm != null ? `Reach: ${f.reachCm} cm` : null)),
    },
    { key: "club", label: "Klub", element: "Club", auto: fighter(corner, (f) => f.club) },
  ];
}

// Official decision: rød vinder fik i vMix en anden (langsommere) animation end
// blå. Begge bruger nu blås: 0,5 s fra midten og ud.
const decision = withStoryboards(decisionSpec, {
  page2: [
    anim("Hidden", "BoxV1", { duration: 0.5, easing: "CubicEasingIn" }),
    anim("Reveal", "BoxV2", { duration: 0.5, easing: "CubicEasingIn" }),
  ],
});

// Countdown: siderne skjuler/viser logo og "countdown"-boks; baren med
// next fight og ur bliver stående. Boksene foldes ned mod baren.
const fold = (reverse: boolean, order: string[][]) =>
  order.flatMap((objects, i) =>
    objects.map((o) =>
      anim("Reveal", o, { delay: i * 0.15, duration: 0.5, easing: "CubicEasingInOut", direction: "Bottom", reverse }),
    ),
  );
const countdown = withStoryboards(countdownSpec, {
  page1: fold(true, [["BoxM1", "Logo"], ["BoxM", "Countdown"]]),
  page2: fold(false, [["BoxM", "Countdown"], ["BoxM1", "Logo"]]),
});

// Tale of the tape. I vMix-titlen stod rød til venstre, men billederne var
// bundet omvendt; her er blå til venstre og rød til højre som i alle de andre
// grafikker. Elementerne hedder stadig "Rød …" til venstre og "Blå …" til højre.
//
// Egen ind-animation (vMix-titlen fløj alt ind på én gang): søjlen bygges op
// nedefra, fighterne glider ind fra siderne, stat-rækkerne foldes ud én ad
// gangen, navnene wipes ud fra midten, og til sidst klub og flag.
// Ud-animationen er vMix-titlens egen.
const EASE = "CubicEasingInOut";
const statRows = [
  ["Blå Boks 1 ", "(O) Height ", "Rød Height", "BLå Height "],
  ["Blå Boks 2 ", "(O) Weight", "Rød Weight ", "Blå Weight "],
  ["Blå Boks 3", "(O) Reach", "Rød Reach", "Blå Reach"],
  ["Blå Boks 4", "(O) Rec", "Rød Rec ", "Blå Rec"],
];
const h2hIn: GtAnim[] = [
  anim("Reveal", "Stor boks ", { duration: 0.6, easing: EASE, direction: "Bottom" }),
  anim("Reveal", "Logo Baggrund", { delay: 0.4, duration: 0.4, easing: EASE, direction: "Top" }),
  anim("Fade", "Logo", { delay: 0.55, duration: 0.4, easing: EASE }),
  anim("Fly", "Rød Kæmper", { delay: 0.2, duration: 0.8, easing: "CubicEasingOut" }),
  anim("Fly", "Blå Kæmper ", { delay: 0.2, duration: 0.8, easing: "CubicEasingOut", direction: "Right" }),
  anim("Fade", "Vægtklassse", { delay: 0.5, duration: 0.4, easing: EASE }),
  anim("Reveal", "Guld Streg", { delay: 0.55, duration: 0.4, easing: EASE, direction: "Center" }),
  ...statRows.flatMap(([box, ...texts], i) => [
    anim("Reveal", box!, { delay: 0.65 + i * 0.08, duration: 0.4, easing: EASE, direction: "Center" }),
    ...texts.map((t) => anim("Fade", t, { delay: 0.8 + i * 0.08, duration: 0.3, easing: EASE })),
  ]),
  anim("Reveal", "Baggrund Titel", { delay: 0.95, duration: 0.4, easing: EASE, direction: "Center" }),
  anim("Fade", "Titel Bund ", { delay: 1.1, duration: 0.3, easing: EASE }),
  // Venstre side wipes fra højre (midten), højre side fra venstre.
  ...["Rød Streg ", "Rød Fornavn ", "Rød Efternavn "].map((o, i) =>
    anim("Reveal", o, { delay: 0.75 + i * 0.06, duration: 0.5, easing: EASE, direction: "Right" }),
  ),
  ...["Blå Streg", "Blå Fornavn ", "Blå Efternavn "].map((o, i) =>
    anim("Reveal", o, { delay: 0.75 + i * 0.06, duration: 0.5, easing: EASE }),
  ),
  ...["Rød klub", "Blå Klub ", "Rød Flag ", "Blå Flag "].map((o) => anim("Fade", o, { delay: 1.1, duration: 0.4, easing: EASE })),
];
const h2h = withStoryboards(recolor(h2hSpec, { "Rød Streg ": "#0070C0", "Blå Streg": "#C00000" }), { in: h2hIn });

const tapeFields = (corner: "blue" | "red", side: "Rød" | "Blå"): FieldDef[] => {
  const label = corner === "blue" ? "Blå" : "Rød";
  const el = (left: string, right: string) => (side === "Rød" ? left : right);
  return [
    { key: `${corner}First`, label: `${label} fornavn`, element: el("Rød Fornavn ", "Blå Fornavn "), auto: fighter(corner, (f) => f.firstName) },
    { key: `${corner}Last`, label: `${label} efternavn`, element: el("Rød Efternavn ", "Blå Efternavn "), auto: fighter(corner, (f) => f.lastName) },
    { key: `${corner}Club`, label: `${label} klub`, element: el("Rød klub", "Blå Klub "), auto: fighter(corner, (f) => f.club) },
    { key: `${corner}Height`, label: `${label} højde`, element: el("Rød Height", "BLå Height "), auto: fighter(corner, (f) => f.heightCm?.toString()) },
    { key: `${corner}Weight`, label: `${label} vægt`, element: el("Rød Weight ", "Blå Weight "), auto: fighter(corner, (f) => (f.weighInKg != null ? dk(f.weighInKg) : null)) },
    { key: `${corner}Reach`, label: `${label} rækkevidde`, element: el("Rød Reach", "Blå Reach"), auto: fighter(corner, (f) => f.reachCm?.toString()) },
    { key: `${corner}Record`, label: `${label} record`, element: el("Rød Rec ", "Blå Rec"), auto: fighter(corner, (f) => f.record) },
  ];
};

export const GRAPHICS: GraphicDef[] = [
  {
    id: "ur",
    label: "Ur",
    group: "Kamp",
    spec: urSpec as unknown as GtSpec,
    pages: { page1: "Skjul navne", page2: "Vis navne" },
    fields: [
      { key: "blue", label: "Blå", element: "Blå Efternavn ", auto: fighter("blue", (f) => f.lastName) },
      { key: "red", label: "Rød", element: "Rød EfterNavn ", auto: fighter("red", (f) => f.lastName) },
      {
        key: "bout",
        label: "Vægtklasse",
        element: "Vægtklasse",
        auto: (c) => (c.data.fight?.weightClass ? `${c.data.fight.weightClass} bout` : null),
      },
      { key: "clock", label: "Rundeur", element: "TID", auto: (c) => clock(timerRemaining(timerState(c, "round"), c.now)) },
    ],
    // Rundemarkøren tegnes af UrExtras i stedet for titlens faste streger.
    dynamic: () => ({ visible: { R1: false, R2: false, R3: false, RoundTekst: false } }),
    Extra: UrExtras,
  },
  {
    id: "statsBlue",
    label: "Fighter blå",
    spec: statsSpec as unknown as GtSpec,
    pages: { page1: "Mål", page2: "Klub" },
    fields: statsFields("blue"),
  },
  {
    id: "statsRed",
    label: "Fighter rød",
    spec: statsSpec as unknown as GtSpec,
    pages: { page1: "Mål", page2: "Klub" },
    fields: statsFields("red"),
  },
  {
    id: "decision",
    label: "Official decision",
    spec: decision,
    pages: { page1: "Blå vinder", page2: "Rød vinder" },
    fields: [
      { key: "blue", label: "Blå", element: "Last Name Blå ", auto: fighter("blue", (f) => f.lastName) },
      { key: "red", label: "Rød", element: "Last Name Rød", auto: fighter("red", (f) => f.lastName) },
      { key: "text", label: "Tekst", element: "Beskrivelse", fallback: "official decision" },
    ],
  },
  {
    // Samme layout som Official decision (den nyeste version af den bar).
    id: "nextMatch",
    label: "Next match",
    spec: decisionSpec as unknown as GtSpec,
    fields: [
      { key: "blue", label: "Blå", element: "Last Name Blå ", auto: nextFighter("blue") },
      { key: "red", label: "Rød", element: "Last Name Rød", auto: nextFighter("red") },
      { key: "text", label: "Tekst", element: "Beskrivelse", fallback: "next match" },
    ],
  },
  {
    id: "countdown",
    label: "Countdown",
    spec: countdown,
    timer: "countdown",
    pages: { page1: "Skjul logo + countdown", page2: "Vis logo + countdown" },
    fields: [
      { key: "title", label: "Overskrift", element: "Countdown", fallback: "countdown" },
      { key: "text", label: "Tekst", element: "NextFight", fallback: "next fight" },
      { key: "clock", label: "Ur", element: "UR", auto: (c) => clock(timerRemaining(timerState(c, "countdown"), c.now), true) },
    ],
  },
  {
    id: "h2h",
    label: "Tale of the tape",
    spec: h2h,
    fields: [
      ...tapeFields("blue", "Rød"),
      ...tapeFields("red", "Blå"),
      { key: "weightClass", label: "Vægtklasse", element: "Vægtklassse", auto: (c) => c.data.fight?.weightClass },
      {
        key: "title",
        label: "Titel",
        element: "Titel Bund ",
        multiline: true,
        auto: (c) => c.data.fight && [c.data.fight.eventName, c.data.fight.eventLocation].filter(Boolean).join("\n"),
      },
    ],
    // Billede og flag: blå til venstre, rød til højre. Mangler de, skjules elementet.
    dynamic: (c) => {
      const f = c.data.fight;
      const images: Record<string, string> = {};
      const visible: Record<string, boolean> = {};
      const set = (el: string, url: string | null | undefined) => {
        if (url) images[el] = url;
        else visible[el] = false;
      };
      set("Rød Kæmper", f?.blue.photo);
      set("Blå Kæmper ", f?.red.photo);
      set("Rød Flag ", flag(f?.blue.country));
      set("Blå Flag ", flag(f?.red.country));
      return { images, visible };
    },
  },
  {
    id: "name",
    label: "Navn + titel",
    group: "Navne",
    spec: nameSpec as unknown as GtSpec,
    presets: "name+title",
    fields: [
      { key: "name", label: "Navn", element: "Navn" },
      { key: "title", label: "Titel", element: "Titel", fallback: "referee" },
    ],
  },
  {
    id: "smallName",
    label: "Lille navn",
    spec: smallNameSpec as unknown as GtSpec,
    presets: "name",
    fields: [{ key: "name", label: "Navn", element: "NavnH" }],
  },
  // Spotter-grafikkerne: stats fra spotterens registreringer i kampen.
  { id: "recap", label: "Round recap", group: "Spotter-stats", Html: Recap, fields: [] },
  { id: "breakdown", label: "Strike breakdown", group: "Spotter-stats", Html: Breakdown, fields: [] },
  { id: "final", label: "Final stats", group: "Spotter-stats", Html: Final, fields: [] },
  {
    id: "flash",
    label: "KD / Sub flash",
    group: "Spotter-stats",
    Html: Flash,
    // Udfyldt med kampens seneste knockdown eller submission.
    fields: [
      { key: "event", label: "Hændelse", element: "event", auto: (c) => c.data.fight?.lastEvent && EVENT_LABELS[c.data.fight.lastEvent.kind] },
      {
        key: "who",
        label: "Fighter",
        element: "who",
        auto: (c) => c.data.fight?.lastEvent && c.data.fight[c.data.fight.lastEvent.corner].name,
      },
      {
        key: "when",
        label: "Runde / tid",
        element: "when",
        auto: (c) => {
          const e = c.data.fight?.lastEvent;
          return e && `Round ${e.round} · ${fmtTime(e.timeSec)}`;
        },
      },
    ],
  },
];

export const GRAPHIC_BY_ID = Object.fromEntries(GRAPHICS.map((g) => [g.id, g])) as Record<GfxGraphicId, GraphicDef>;

export interface ResolvedField {
  def: FieldDef;
  value: string;
  auto: string; // det der vises uden manuel tekst
  manual: boolean;
}

// Tekster og synlighed for en grafik ud fra manuelle felter og kampdata.
export function resolve(def: GraphicDef, manual: Record<string, string>, ctx: GfxCtx) {
  const dyn = def.dynamic?.(ctx) ?? {};
  const texts: Record<string, string> = { ...dyn.texts };
  const fields: ResolvedField[] = def.fields.map((f) => {
    const auto = f.auto?.(ctx) || f.fallback || "";
    const isManual = manual[f.key] != null && manual[f.key] !== "";
    const value = isManual ? manual[f.key]! : auto;
    texts[f.element] = f.multiline ? value.replace(/\s*\|\s*/g, "\n") : value;
    return { def: f, value, auto: f.multiline ? auto.replace(/\n/g, " | ") : auto, manual: isManual };
  });
  return { texts, visible: dyn.visible ?? {}, images: dyn.images ?? {}, fields };
}
